import { intercomService } from "@services/intercomService";
import {
  WSAlert,
  WSAlertProps,
  WSButton,
  WSElementProps
} from "@wingspanhq/fe-component-library";
import React, { useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { PATH_SETTINGS_ACCOUNT_ACCOUNT } from "../../../modules/Settings/paths";
import { ErrorContext } from "../../utils/errorMap";
import { resolveErrorMessage } from "../../utils/resolveErrorMessage";

const OTP_SUPPORT_MESSAGE =
  "Hi there! I'm having trouble with completing two-factor authentication. Could you help me with it";

/**
 * The v1 users-service signals "this call needs a fresh 2FA challenge" through
 * the error subtype rather than a status code. It gets its own alert because
 * the recovery steps, not the message, are what the user needs.
 */
const isOtpRequiredError = (error: unknown): boolean => {
  const data = (error as any)?.response?.data;
  return (data?.subtype || data?.errorSubType) === "OtpRequired";
};

export type ErrorAlertProps = {
  error?: unknown;
  context: ErrorContext;
  forceShowRawError?: boolean;
} & WSElementProps &
  WSAlertProps;

export const ErrorAlert: React.FC<ErrorAlertProps> = props => {
  const { error, context, forceShowRawError, ...alertProps } = props;
  const history = useHistory();
  const [isDismissed, setIsDismissed] = useState(false);
  const prevErrorRef = useRef(error);

  if (error !== prevErrorRef.current) {
    prevErrorRef.current = error;
    if (isDismissed) {
      setIsDismissed(false);
    }
  }

  const message = useMemo(
    () => resolveErrorMessage({ error, context, forceShowRawError }),
    [context, error, forceShowRawError]
  );

  if (!error || isDismissed) {
    return null;
  }

  if (isOtpRequiredError(error)) {
    return (
      <WSAlert
        {...alertProps}
        theme="error"
        icon="info-circle"
        title="Verification incomplete"
        onDismiss={() => setIsDismissed(true)}
        data-testid="error-alert"
      >
        <>
          Two-factor verification is required to continue. Please try again.
          <br />
          Still having issues?
          <br />
          {"· Check that your phone number and email are up to date. "}
          <WSButton.Link
            onClick={() => history.push(PATH_SETTINGS_ACCOUNT_ACCOUNT)}
            type="button"
          >
            Go to account settings
          </WSButton.Link>
          <br />
          {"· Need help? "}
          <WSButton.Link
            onClick={() => intercomService.openWithMessage(OTP_SUPPORT_MESSAGE)}
            type="button"
          >
            Contact support
          </WSButton.Link>
        </>
      </WSAlert>
    );
  }

  return (
    <WSAlert
      {...alertProps}
      theme="error"
      icon="info-circle"
      data-testid="error-alert"
    >
      <>{message}</>
    </WSAlert>
  );
};
